import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Trash2, Plus, Pencil, X, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRONOUNS = [
  { id: 'han', label: 'han' },
  { id: 'hon', label: 'hon' },
  { id: 'hen', label: 'hen' },
];

function PersonaForm({ pin, initial, onSaved, onClose }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    pronoun: initial?.pronoun || 'han',
    what_they_did: initial?.what_they_did || '',
    when: initial?.when || '',
    include_admiration: !!initial?.include_admiration,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Namn krävs'); return; }
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.post(`${API}/closure/personas/${initial.id}/update`,
          { pin, ...form }, { withCredentials: true });
      } else {
        await axios.post(`${API}/closure/personas`,
          { pin, ...form }, { withCredentials: true });
      }
      toast.success('Sparat 🪞');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="closure-persona-modal">
      <div className="neo-card bg-white dark:bg-[#252545] max-w-lg w-full max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
            🪞 {initial?.id ? 'Redigera person' : 'Lägg till person'}
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="closure-persona-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[11px] opacity-70 mb-3">
          Allt krypteras med din PIN. Ingen riktig person kontaktas — det här är ditt rum för upprättelse 💗
        </p>

        <label className="block mb-2 text-xs font-bold">Namn
          <input value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={60}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white"
            data-testid="closure-name-input" />
        </label>

        <div className="mb-2">
          <span className="text-xs font-bold block mb-1">Pronomen</span>
          <div className="flex gap-2">
            {PRONOUNS.map((p) => (
              <button key={p.id} type="button" onClick={() => set('pronoun', p.id)}
                className={`px-3 py-1.5 rounded-full border-2 border-black text-xs font-bold transition ${
                  form.pronoun === p.id ? 'bg-[#CDB4DB] text-white' : 'bg-white'
                }`}
                data-testid={`closure-pronoun-${p.id}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <label className="block mb-2 text-xs font-bold">Vad de gjorde mot dig
          <textarea value={form.what_they_did} onChange={(e) => set('what_they_did', e.target.value)} rows={4}
            maxLength={1500}
            placeholder="Så detaljerat eller kort du vill. Inget är för litet, inget är för stort."
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white resize-none"
            data-testid="closure-what-input" />
        </label>

        <label className="block mb-3 text-xs font-bold">När (gymnasiet, högstadiet, jobbet…)
          <input value={form.when} onChange={(e) => set('when', e.target.value)} maxLength={120}
            className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black bg-white"
            data-testid="closure-when-input" />
        </label>

        <label className="flex items-start gap-2 mb-4 cursor-pointer p-3 rounded-xl bg-[#FFF5F7] dark:bg-[#2a1a2e] border-2 border-black/10">
          <input type="checkbox" checked={form.include_admiration}
            onChange={(e) => set('include_admiration', e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-rose-500"
            data-testid="closure-admiration-toggle" />
          <span className="text-xs">
            <b>Låt hen ibland säga vad de såg i mig då</b><br />
            <span className="opacity-70">T.ex. &quot;Du var smartare än mig och det skrämde mig.&quot; — aldrig som ursäkt.</span>
          </span>
        </label>

        <button onClick={save} disabled={saving}
          className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold disabled:opacity-50"
          data-testid="closure-persona-save">
          {saving ? 'Sparar…' : 'Spara'}
        </button>
      </div>
    </div>
  );
}

function ChatView({ pin, persona, onBack, onEdit }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/closure/history`,
        { pin, persona_id: persona.id }, { withCredentials: true });
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda');
    } finally {
      setLoading(false);
    }
  }, [pin, persona.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const newHist = [...messages, { role: 'user', text }];
    setMessages(newHist);
    setInput('');
    try {
      const { data } = await axios.post(`${API}/closure/chat`, {
        pin, persona_id: persona.id, message: text,
        history: messages.slice(-12).map((m) => ({ role: m.role, text: m.text })),
      }, { withCredentials: true });
      setMessages([...newHist, { role: 'them', text: data.reply || '' }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte skicka');
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!window.confirm(`Radera hela konversationen med ${persona.name}?`)) return;
    try {
      await axios.post(`${API}/closure/clear`,
        { pin, persona_id: persona.id }, { withCredentials: true });
      setMessages([]);
      toast.success('Rensat');
    } catch (e) {
      toast.error('Kunde inte rensa');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Öppnar rummet…</div>;

  return (
    <div className="space-y-3" data-testid="closure-chat-view">
      <div className="neo-card p-4 bg-gradient-to-br from-[#F5EBFF] to-[#FFE4E9] dark:from-[#2a1a3a] dark:to-[#1f1428]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="neo-btn p-2 bg-white" data-testid="closure-back-btn">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="w-12 h-12 rounded-full border-4 border-black bg-gradient-to-br from-purple-300 to-rose-400 flex items-center justify-center text-2xl">
              🪞
            </div>
            <div>
              <h2 className="text-xl font-bold font-['Fredoka']">{persona.name}</h2>
              <p className="text-[11px] opacity-70 max-w-xs leading-tight">
                {persona.when || 'Ditt rum för upprättelse'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onEdit} className="neo-btn px-3 py-2 bg-[#FFE8A3] text-xs font-bold flex items-center gap-1"
              data-testid="closure-edit-btn">
              <Pencil className="w-3.5 h-3.5" /> Redigera
            </button>
            <button onClick={clear} className="neo-btn px-3 py-2 bg-[#FFB3B3] text-xs font-bold flex items-center gap-1"
              data-testid="closure-clear-btn">
              <Trash2 className="w-3.5 h-3.5" /> Rensa
            </button>
          </div>
        </div>
      </div>

      <div ref={scrollerRef}
        className="neo-card p-4 bg-[#FFFAF7] dark:bg-[#1f1428] h-[58vh] overflow-y-auto space-y-3"
        data-testid="closure-messages">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="text-5xl mb-2">🪞</div>
            <p className="font-bold font-['Fredoka'] text-purple-700">Säg det du behövt säga.</p>
            <p className="text-xs opacity-70 mt-1 max-w-sm">
              Skäll, gråt, fråga &quot;varför&quot;, eller skriv bara &quot;berätta vad du gjorde&quot;. Du får ditt förlåt.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`closure-msg-${i}`}>
            <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl border-2 border-black shadow-[2px_2px_0_#000] text-sm font-['Nunito'] whitespace-pre-wrap break-words ${
              m.role === 'user' ? 'bg-[#FFC8DD] rounded-br-sm' : 'bg-white dark:bg-[#2d2d54] rounded-bl-sm'
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-2xl border-2 border-black bg-white shadow-[2px_2px_0_#000] text-sm italic opacity-70">
              {persona.name} svarar…
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 items-end">
        <textarea value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Säg det du burit på till ${persona.name}…`}
          rows={2}
          className="flex-1 px-3 py-2 rounded-2xl border-2 border-black bg-white dark:bg-[#2d2d54] text-sm resize-none"
          data-testid="closure-input" />
        <button onClick={send} disabled={sending || !input.trim()}
          className="neo-btn px-4 py-3 bg-gradient-to-r from-purple-400 to-rose-400 text-white font-bold flex items-center gap-1 disabled:opacity-50"
          data-testid="closure-send-btn">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function ClosureRoom({ pin }) {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activePersona, setActivePersona] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/closure/personas/list`,
        { pin }, { withCredentials: true });
      setPersonas(data.personas || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte ladda');
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => { loadList(); }, [loadList]);

  const remove = async (p) => {
    if (!window.confirm(`Ta bort ${p.name} och all chatt? Detta går inte att ångra.`)) return;
    try {
      await axios.post(`${API}/closure/personas/${p.id}/delete`,
        { pin }, { withCredentials: true });
      toast.success('Borttagen');
      await loadList();
    } catch (e) {
      toast.error('Kunde inte ta bort');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Öppnar Upprättelse-rummet…</div>;

  if (activePersona) {
    const fresh = personas.find((p) => p.id === activePersona.id) || activePersona;
    return (
      <>
        <ChatView pin={pin} persona={fresh}
          onBack={() => setActivePersona(null)}
          onEdit={() => setEditing(fresh)} />
        {editing && (
          <PersonaForm pin={pin} initial={editing}
            onSaved={loadList}
            onClose={() => setEditing(null)} />
        )}
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="closure-room">
      <div className="neo-card p-5 bg-gradient-to-br from-[#F5EBFF] to-[#FFE4E9] dark:from-[#2a1a3a] dark:to-[#1f1428]">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-full border-4 border-black bg-white flex items-center justify-center text-3xl shrink-0">
            🪞
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold font-['Fredoka']">Upprättelse</h2>
            <p className="text-xs opacity-75 mt-0.5 max-w-md">
              Ett tryggt rum där du får höra ett rent förlåt — utan bortförklaringar. Lägg till personer som har sårat dig, så får de svara så som de borde ha gjort. Allt krypterat med din PIN. 💜
            </p>
          </div>
        </div>
      </div>

      <button onClick={() => setAdding(true)}
        className="neo-btn w-full py-3 bg-[#FFC8DD] font-bold flex items-center justify-center gap-2"
        data-testid="closure-add-btn">
        <Plus className="w-4 h-4" /> Lägg till person
      </button>

      {personas.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white">
          <div className="text-4xl mb-2">🌿</div>
          <p className="font-bold font-['Fredoka']">Ditt rum är tomt än så länge</p>
          <p className="text-xs opacity-75 mt-1">Lägg till första personen så öppnas chatten.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {personas.map((p) => (
            <div key={p.id} className="neo-card p-4 bg-white dark:bg-[#252545] flex flex-col gap-2"
              data-testid={`closure-persona-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold font-['Fredoka'] truncate">{p.name}</p>
                  <p className="text-[10px] opacity-60">{p.pronoun} · {p.when || '—'}</p>
                </div>
                <button onClick={() => setEditing(p)} className="neo-btn p-1.5 bg-white" title="Redigera"
                  data-testid={`closure-persona-edit-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(p)} className="neo-btn p-1.5 bg-[#FF6B6B] text-white" title="Ta bort"
                  data-testid={`closure-persona-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {p.what_they_did && (
                <p className="text-xs opacity-70 line-clamp-2 italic">&quot;{p.what_they_did}&quot;</p>
              )}
              <button onClick={() => setActivePersona(p)}
                className="neo-btn mt-1 px-3 py-2 bg-gradient-to-r from-purple-400 to-rose-400 text-white text-xs font-bold"
                data-testid={`closure-open-${p.id}`}>
                Öppna chatt
              </button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <PersonaForm pin={pin} initial={null}
          onSaved={loadList}
          onClose={() => setAdding(false)} />
      )}
      {editing && !activePersona && (
        <PersonaForm pin={pin} initial={editing}
          onSaved={loadList}
          onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
